import React from 'react';

const SocialLinksBubbles = ({
  socialLinks = {},
  chessLinks = {},
  primarySocialProfile = null,
  onChessPlatformClick = null,
  onDonationClick = null,
  formData = {}
}) => {
  const platforms = [
    // Chess Platforms
    {
      key: 'chessCom',
      name: 'Chess.com',
      customImage: '/Images/Chesscom.png',
      url: (username) => `https://chess.com/member/${username}`,
      value: chessLinks.chessCom
    },
    {
      key: 'lichess',
      name: 'Lichess',
      customImage: '/Images/LiChess.png',
      url: (username) => `https://lichess.org/@/${username}`,
      value: chessLinks.lichess
    },
    {
      key: 'fideId',
      name: 'FIDE',
      customImage: '/Images/FIDE.png',
      url: (fideId) => `https://ratings.fide.com/profile/${fideId}`,
      value: chessLinks.fideId
    },
    // Social Media Platforms
    {
      key: 'youtube',
      name: 'YouTube',
      icon: 'fa-youtube',
      bgColor: 'bg-red-600',
      url: (username) => username.startsWith('http') ? username : `https://youtube.com/@${username}`,
      value: socialLinks.youtube
    },
    {
      key: 'twitter',
      name: 'Twitter',
      icon: 'fa-x-twitter',
      bgColor: 'bg-black',
      url: (username) => username.startsWith('http') ? username : `https://twitter.com/${username}`,
      value: socialLinks.twitter
    },
    {
      key: 'facebook',
      name: 'Facebook',
      icon: 'fa-facebook',
      bgColor: 'bg-[#1877F2]',
      url: (username) => username.startsWith('http') ? username : `https://facebook.com/${username}`,
      value: socialLinks.facebook
    },
    {
      key: 'instagram',
      name: 'Instagram',
      icon: 'fa-instagram',
      bgColor: 'bg-gradient-to-tl from-purple-900 to-pink-400',
      url: (username) => username.startsWith('http') ? username : `https://instagram.com/${username}`,
      value: socialLinks.instagram
    },
    {
      key: 'tiktok',
      name: 'TikTok',
      icon: 'fa-tiktok',
      bgColor: 'bg-black',
      url: (username) => username.startsWith('http') ? username : `https://tiktok.com/@${username}`,
      value: socialLinks.tiktok
    },
    {
      key: 'twitch',
      name: 'Twitch',
      icon: 'fa-twitch',
      bgColor: 'bg-purple-500',
      url: (username) => username.startsWith('http') ? username : `https://twitch.tv/${username}`,
      value: socialLinks.twitch
    },
    {
      key: 'discord',
      name: 'Discord',
      icon: 'fa-discord',
      bgColor: 'bg-indigo-600',
      url: (link) => link.startsWith('http') ? link : `https://discord.gg/${link}`,
      value: socialLinks.discord
    },
    // Donations
    {
      key: 'donations',
      name: 'Support Me',
      icon: 'fa-sack-dollar',
      bgColor: 'bg-green-600',
      value: formData?.donationsEnabled && formData?.stripeAccountId,
      isDonation: true
    }
  ];

  const activePlatforms = platforms.filter(platform => platform.value);

  // Sort platforms to put primary social first
  const sortedPlatforms = activePlatforms.sort((a, b) => {
    if (a.key === primarySocialProfile) return -1;
    if (b.key === primarySocialProfile) return 1;
    return 0;
  });

  if (activePlatforms.length === 0) {
    return null;
  }

  return (
    <div className="w-full max-w-2xl mx-auto mb-8">
      <div className="flex flex-wrap justify-center gap-4 ">
        {sortedPlatforms.map((platform) => {
          const isPrimary = platform.key === primarySocialProfile;
          const isChessPlatform = platform.key === 'chessCom' || platform.key === 'lichess';
          const isDonation = platform.isDonation;

          // Handle donations with special click behavior
          if (isDonation) {
            if (onDonationClick) {
              return (
                <button
                  key={platform.key}
                  onClick={onDonationClick}
                  className="group relative"
                  title={platform.name}
                >
                  <div
                    className={`w-12 h-12 rounded-full ${platform.bgColor} flex items-center justify-center transform transition-all duration-200 hover:scale-110 hover:shadow-lg`}
                  >
                    <i className={`fas ${platform.icon} text-white text-2xl`}></i>
                  </div>
                  {/* Tooltip */}
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    {platform.name}
                  </div>
                </button>
              );
            }
            // If no onDonationClick handler, skip rendering donations
            return null;
          }

          // Handle chess platforms with special click behavior
          if (isChessPlatform && onChessPlatformClick) {
            return (
              <button
                key={platform.key}
                onClick={() => onChessPlatformClick(platform.key)}
                className="group relative"
                title={platform.name}
              >
                <div
                  className={`w-12 h-12 rounded-full ${platform.bgColor} flex items-center border-none justify-center transform transition-all duration-200 hover:scale-110 hover:shadow-lg overflow-hidden`}
                >
                  {platform.customImage ? (
                    <img
                      src={platform.customImage}
                      alt={platform.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <i className={`fab ${platform.icon} text-white text-2xl`}></i>
                  )}
                </div>
                {/* Tooltip */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                  {platform.name}
                </div>
              </button>
            );
          }
          
          // Regular social platforms with external links
          // Safety check: ensure platform has a url function
          if (!platform.url || typeof platform.url !== 'function') {
            return null;
          }
          
          return (
            <a
              key={platform.key}
              href={platform.url(platform.value)}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative"
              title={platform.name}
            >
              <div
                className={`w-12 h-12 rounded-full ${platform.bgColor} flex items-center justify-center transform transition-all duration-200 hover:scale-110 hover:shadow-lg overflow-hidden`}
              >
                {platform.customImage ? (
                  <img
                    src={platform.customImage}
                    alt={platform.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <i className={`fab ${platform.icon} text-white text-2xl`}></i>
                )}
              </div>
              {/* Tooltip */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                {platform.name}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default SocialLinksBubbles;